import { z } from "zod";
import { defineEvent } from "./_define";
import { baseEnvelopeSchema } from "./_envelope";

/**
 * Owlet Night Watch intake analytics. The intake's Felt engagement questions
 * (asked once, on the account's first arm) plus the breathing safety gate never
 * reach Blueberry — they surface here so the funnel is interpretable.
 *
 * `question_set_version` mirrors the intake's `QUESTION_SET_VERSION` so answers
 * stay interpretable across question-set edits without an event_version bump:
 * a set edit changes the meaning of the answer keys, and the stamp records
 * which set produced a given payload.
 *
 * Client metadata is stringly-typed (`Record<string, string>`), so the answer
 * values arrive as option ids ("peace", "once", …) and the version as its
 * decimal string.
 */

const feltIntakeEnvelope = baseEnvelopeSchema
  .extend({
    /** Mirrors the intake's QUESTION_SET_VERSION (decimal string on client). */
    question_set_version: z.union([z.string(), z.number()]),
  })
  .passthrough();

/**
 * Fires once per successful arm, carrying the Felt engagement answers. No
 * `value` — an arm has no categorical discriminator; it's counted by name.
 */
export const nightwatchArmed = defineEvent({
  eventName: "nightwatch_armed",
  eventVersion: 1,
  valueSchema: z.union([z.string(), z.number()]).optional(),
  metadataSchema: feltIntakeEnvelope.extend({
    /** felt-q1 — what brings you to Night Watch (option id). */
    felt_q1: z.string().optional(),
    /** felt-q2 — how often you check on the baby (option id). */
    felt_q2: z.string().optional(),
    /** The breathing-gate answer that let the arm proceed ("no" / "dontKnow"). */
    breathing: z.string().optional(),
  }),
});

export const NIGHTWATCH_HARD_STOP_VALUES = {
  DIFFICULTY_BREATHING: "difficulty_breathing",
} as const;
export type NightwatchHardStopValue =
  (typeof NIGHTWATCH_HARD_STOP_VALUES)[keyof typeof NIGHTWATCH_HARD_STOP_VALUES];

/**
 * Fires when a hard stop ends the intake without arming (the breathing gate
 * routes to the seek-care screen). Distinct from {@link nightwatchArmed}: it
 * fires at a different point (no arm) and is a safety-triage signal in its own
 * right. `value` is the triggering gate so it can be counted by reason.
 */
export const nightwatchIntakeHardStop = defineEvent({
  eventName: "nightwatch_intake_hard_stop",
  eventVersion: 1,
  valueSchema: z
    .enum([NIGHTWATCH_HARD_STOP_VALUES.DIFFICULTY_BREATHING])
    .or(z.string()),
  metadataSchema: feltIntakeEnvelope,
});
